import React, { useState, useEffect } from 'react';
import clsx from 'clsx';
import { ModalFooter } from '../elements/modalFooter';
import { TextField } from '../ui';
import { Formik, FieldArray } from 'formik';
import { PlusIcon, TrashIcon } from '../../icons';
import Select from 'react-select';
import { 
    useAddGroup, 
    useUpdateGroup,
    useAssignMemberToGroup, 
    useUnassignMemberToGroup } from '../../hooks/react-query/useCompanyGroup';
import { useGetUerList } from '../../hooks/react-query/useAdmin';
import { Mapper } from '../../utils/user/mapper';
import Loader from '../../components/elements/loader';
import { queryClient } from '../../utils/queryClient';
import { useQuery } from '@tanstack/react-query';
import { QUERY_KEYS } from '../../config/constants';
import { ConfirmationDialog } from '../ui';
import { toast } from '../../components/ui/Toast/useToast';
import { createShadowUserFn, getCompanyGroupByIdFn } from '../../transactions/user/groupAdmin';
import { Dialog, DialogContent, DialogTrigger } from '../ui/Modal';
import { set } from 'lodash';

const {COMPANY_GROUPS,COMPANY_GROUPS_DETAILS} = QUERY_KEYS;

export const CompanyGroupForm = ({
    mode = "create",
    group,
    handleClose,
    managementFilterState
}) => {

    const [ modalMode, setModalMode] = useState(mode || "create");
    const [ groupId, setGroupId ] = useState(mode==='edit' && group?.id);
    const [ groupFleetAdmins, setGroupFleetAdmins] = useState({data:[]})
    const [ members, setMembers ] = useState([{user:{afm:"",company:""}}]);
    const [ groupForm, setGroupForm ] = useState({name:"", group_fleet_admin:{email:""}});
    const [ filterState, setFilterState ] = useState({
        length: 25,
        orderType: 'asc',
        orderBy: 'id',
        search: '',
        page: 1,
      });
    const [ unassignOpenModal, setUnassignOpenModal] = useState(false);
    const [ failedShadows, setFailedShadows ] = useState([]);
    const [ vatErrors, setVatErrors ] = useState({});
    const [ companyErrors, setCompanyErrors ] = useState({});
    const [ shadowToUnassign, setShadowToUnassign ] = useState({});

    const {data:groupDetail, isLoading: isGroupLoading} = useQuery({
        queryKey: [COMPANY_GROUPS_DETAILS, groupId],
        queryFn: () => getCompanyGroupByIdFn(groupId),
        enabled: mode === 'edit',
        staleTime: Infinity,
        cacheTime: Infinity,
        refetchOnWindowFocus: false
    });

    const { data: users } = useGetUerList(filterState);
    const { mutate: addGroup, isLoading: isAddingLoading } = useAddGroup();
    const { mutate: updateGroup, isLoading: isUpdatingLoading } = useUpdateGroup();
    const { mutate: assignMembersToGroup, isLoading: isAssignMemberLoading } = useAssignMemberToGroup();
    const { mutate: unassignMembersToGroup, isLoading: isUnassignMemberLoading } = useUnassignMemberToGroup();
    
      
    useEffect(() => {
        if (users && users.pages && users.pages[0].data) {
            setGroupFleetAdmins(Mapper(users.pages[0], 'users'));
        }
    }, [users]);

    useEffect(() => {
        if (groupDetail && groupDetail.data && groupDetail.data.members) {
            setGroupForm({name:groupDetail.data.name, group_fleet_admin:{email:groupDetail.data.group_fleet_admin?.email ?? ""}})
            setMembers(groupDetail.data.members)
        }
    }, [groupDetail]);

    const handleSearch = (searchKey) => {
        setFilterState((prev) => ({ ...prev, search: searchKey, page: 1 }));
    };

    const handleAddGroup = async () => {
        let newid = 0;
        await new Promise((resolve) => {
            addGroup({name:groupForm?.name, groupFleetAdminUserId:groupForm.group_fleet_admin?.id}, {
                onSuccess: (response) => {
                    const statusFail = response.data?.status === "fail"
                    if(!statusFail){
                        toast({
                            variant: 'success',
                            description: "Το group δημιουργήθηκε επιτυχώς",
                        });
                        setGroupId(response.data.companyGroup.id)
                        setGroupForm({name:response.data.companyGroup.name, group_fleet_admin:{email:response.data.companyGroup.group_fleet_admin.email}})
                        invalidateData();
                        resolve(response.data.companyGroup.id);
                    } else {
                        if(!!response.exceptions.invalid.groupFleetAdminUserId && response.exceptions.invalid.groupFleetAdminUserId.length > 0){
                            toast({
                                variant: 'destructive',
                                description: response.exceptions.invalid.groupFleetAdminUserId[0] || "Something went wrong.",
                            });
                        } else if(!!response.exceptions.invalid.name && response.exceptions.invalid.name.length > 0){
                            toast({
                                variant: 'destructive',
                                description: response.exceptions.invalid.name[0] || "Something went wrong.",
                            });
                        }
                    };
                }
            })
        }).then((result) => {
            newid = result
        })
        return newid;
    }

    const handleEditGroup = () => {
        updateGroup({groupId: groupId, payload:{name:groupForm.name}}, {
            onSuccess: (response) => {
                invalidateData();
                if(response.data?.status === 'fail') {
                    if(!!response.exceptions.invalid.name && response.exceptions.invalid.name.length > 0){
                        toast({
                            variant: 'destructive',
                            description: response.exceptions.invalid.name[0] || "Something went wrong.",
                        });
                    }
                } else {
                    toast({
                        variant: 'success',
                        description: "To Group ενημερώθηκε επιτυχώς.",
                    });
                }
            }
        })
    }

    const handleCreateandAssignMembers = async (shadowsList, groupid) => {
        const vatErrors = {};
        const companyErrors = {};
        const assignids = [];
        const failedMembers = [];

        const results = await Promise.allSettled(
            shadowsList.map((shadow) =>
                createShadowUserFn({ afm: shadow.user.afm, company: shadow.user.company })
            )
        );

        // Map results to their corresponding shadows
        results.forEach((result, index) => {
            if (result.status === "fulfilled") {
                //handle exceptions
                const failed = (result.value.data.status === "fail")
                // store the exception messages
                if(failed && result.value.exceptions ) {
                    // toast the failed shadows
                    failedMembers.push(shadowsList[index])
                    if(!!result.value.exceptions.invalid.afm && result.value.exceptions.invalid.afm.length > 0) {
                        vatErrors[shadowsList[index].user.afm] = result.value.exceptions.invalid.afm[0]
                        toast({
                            variant: "destructive",
                            description: `Failed to create shadow for AFM: ${shadowsList[index].user.afm}`,
                        });
                    } else if (!!result.value.exceptions.invalid.company && result.value.exceptions.invalid.company.length > 0) {
                        companyErrors[shadowsList[index].user.company] = result.value.exceptions.invalid.company[0]
                        toast({
                            variant: "destructive",
                            description: `Failed to create shadow for AFM: ${shadowsList[index].user.afm}`,
                        });
                    } else {
                        toast({
                            variant: "destructive",
                            description: `Παρουσιάστηκε κάποιο πρόβλημα`,
                        });
                    }
                } else if (!failed && !!result.value.data.user) {
                    assignids.push(result.value.data.user.id)
                }
                
            } else if (result.status === "rejected") {
                // Handle rejected promises
                toast({
                    variant: "destructive",
                    description: `Failed to create shadow for AFM: ${shadowsList[index].user.afm}`,
                });
            }
        });

        // Store the failed members to the state
        setFailedShadows(failedMembers)
        setVatErrors(vatErrors);
        setCompanyErrors(companyErrors);

        //assign successfull shadow members
        assignids.length > 0 && assignMembersToGroup(
            {groupid: groupid, payload: {companyGroupId: groupid, userIds: assignids}}, 
            {onSuccess: (response) => {
                if(response && response.exceptions.length === 0){
                    invalidateData();
                    toast({
                        variant: 'success',
                        description: "Τα νέα μέλη καταχωρήθηκαν επιτυχώς.",
                      });
                    setModalMode('edit')
                } else {
                    toast({
                        variant: 'destructive',
                        description: "Something went wrong",
                      });
                };
            }}
        )
    }

    const handleUnassignGroupMember = (shadow) => {
        unassignMembersToGroup(
            {groupid: shadow.companyGroupId, payload: {companyGroupId: shadow.companyGroupId, userIds: [shadow.user.id]}}, 
            {onSuccess: (response) => {
                invalidateData();
                if(!!response.exceptions && response.exceptions.length > 0){
                    toast({
                        variant: 'destructive',
                        description: response.exceptions[0] || "Something went wrong.",
                    });
                }
                setUnassignOpenModal(false);
            }}
        )
    }

    const invalidateData = () => {
        queryClient.invalidateQueries({
            queryKey: [COMPANY_GROUPS]
        })
        queryClient.invalidateQueries({
            queryKey: [COMPANY_GROUPS_DETAILS, groupId],
        });
        !!managementFilterState && queryClient.invalidateQueries({
            queryKey: [COMPANY_GROUPS, managementFilterState],
        });
    }

    const getAddedMembers = (values) => {
        // Find added members
        const addedMembers = values.members.filter(
            (valueMember) =>
                !members.some(
                    (stateMember) =>
                        stateMember.user.afm === valueMember.user.afm &&
                        stateMember.user.company === valueMember.user.company
                )
        );

        return [...addedMembers];
    };

    return (
        <>
        {(isAddingLoading || isUpdatingLoading || (isGroupLoading && mode === 'edit') ) && <Loader/>}
        <div className=''>
            <div className='max-h-[70vh]'>
                <div className="flex gap-3 items-center flex-col w-full bg-white lg:py-10 py-5 lg:pb-6 lg:px-6 rounded-t-[20px]">
                    <h3 className="max-lg:place-self-start">
                        {modalMode === "edit" ? "Ενημέρωση Στοιχείων" : "Προσθήκη Νέου Company Group"}
                    </h3>
                </div>
                <div className="grid sm:grid-cols-2 gap-10 pb-5 lg:min-w-[600px]">
                    <div className=''>
                        <label htmlFor={groupForm?.name} className='text-label text-fadedgrey'>
                            Όνομα Group
                        </label>
                        <TextField 
                            disabled={(isAddingLoading || isUpdatingLoading)}
                            id={"name"}
                            autoComplete="off"
                            value={ groupForm.name }
                            placeholder={modalMode==="create"? "e.g. Avis Group" : ""}
                            onChange={(e) => {
                                setGroupForm({...groupForm, name: e.target.value});
                            }}
                        />
                    </div>
                    <div className='overflow-visible' key={groupForm.group_fleet_admin}>
                        <label
                            htmlFor={groupForm?.group_fleet_admin.email}
                            className="text-label text-fadedgrey"
                        >
                            Group Fleet Admin
                        </label>
                        <Select
                            isDisabled={ modalMode==="edit" || (isAddingLoading || isUpdatingLoading)}
                            id="groupFleetAdmin"
                            key={"groupFleetAdmin"}
                            options={groupFleetAdmins.data.map((gfa) => ({value: gfa.email, label: gfa.email, name: gfa.username, id: gfa.id}))}
                            value={ groupForm.group_fleet_admin && groupForm.group_fleet_admin.email
                                ? { value: groupForm.group_fleet_admin.email, label: groupForm.group_fleet_admin.email }
                                : null
                            }
                            onChange={(selectedOption) => {
                                setGroupForm({ ...groupForm, group_fleet_admin:{email: selectedOption.value, id: selectedOption.id }});
                            }}
                            onInputChange={(input) => {handleSearch(input)}}
                            placeholder="Παρακαλώ επιλέξτε"
                            isSearchable
                            onMenuScrollToBottom={() => {
                                filterState.search === "" && setFilterState((prev) => ({ ...prev,  length: prev.length+25 }));
                            }}
                            styles={{
                                menuList: (provided) => ({
                                    ...provided,
                                    maxHeight: '150px',
                                    overflowY: 'auto',
                                    position: 'popper',
                                })
                            }}
                        />
                    </div>
                </div>
                <h3 className='py-4'>Μέλη</h3>
                <div className='grid grid-cols-2 items-center gap-2'>
                    <label htmlFor={groupForm.title} className='text-label text-fadedgrey pl-3'>
                        Επωνυμία
                    </label>
                    <label htmlFor={groupForm.title} className='text-label text-fadedgrey'>
                        Α.Φ.Μ.
                    </label>
                </div>
                <Formik
                    initialValues={{ members: [...members, ...failedShadows]}}
                    enableReinitialize={true}
                    validateOnChange={true}
                    onSubmit={async (values) => {
                        const addedMembers = getAddedMembers(values);
                        if(modalMode === 'create' && !!groupForm.name && !!groupForm.group_fleet_admin.id) {
                            const newGroupid = await handleAddGroup();
                            if(!!newGroupid && newGroupid !== 0 && addedMembers.length>0){
                                await handleCreateandAssignMembers(addedMembers, newGroupid);
                            }
                            setModalMode('edit')
                            invalidateData();
                            handleClose();
                        } else if (modalMode === 'edit') {
                            if ( addedMembers.length === 0 && groupForm.name === groupDetail.data.name) {
                                toast({
                                    variant: 'destructive',
                                    description: "Δεν υπάρχουν αλλαγές για ενημέρωση."
                                    });
                            }
                            if(groupForm.name !== groupDetail.data.name){
                                handleEditGroup();
                                invalidateData();
                                handleClose();
                            }
                            if(addedMembers.length > 0 ) {
                                await handleCreateandAssignMembers(addedMembers, groupDetail.data.id);
                                invalidateData();
                                handleClose();
                            }
                        } else {
                            toast({
                                variant: 'destructive',
                                description: "Something went wrong."
                                });
                        }
                    }}
                >
                    {({ values, setFieldValue, handleSubmit, errors, touched }) => (
                        <form onSubmit={handleSubmit}>
                            <>
                                {( isAssignMemberLoading || isUnassignMemberLoading) && <Loader/>}    
                                <FieldArray name="members"
                                    render = { arrayHelpers => (
                                        <div className='flex flex-col gap-2'>
                                        {values.members.map((shadow, index) => (
                                            <div key={`member-${index}`} className='flex items-center gap-2'>
                                                <div className={`flex-grow size-14 rounded-[8px] border border-[#A5A5A5] 
                                                    ${(!!companyErrors[values.members[index].user.afm])? "border-primary border-2" : "border-[#A5A5A5]"} p-2 bg-white text-[#A5A5A5]`}>
                                                    <input 
                                                        disabled={!!shadow.id || (isAddingLoading || isUpdatingLoading)}
                                                        className="outline-0 w-full text-black placeholder:text-label"
                                                        name={`company-${index}`}
                                                        value={values.members[index].user.company}
                                                        onChange={(e) =>
                                                            setFieldValue(
                                                                `members[${index}].user.company`,
                                                                e.target.value
                                                            )
                                                        }
                                                        placeholder={`π.χ. Avis`} 
                                                    />
                                                    {errors.members?.[index]?.user?.company && touched.members?.[index]?.user?.company && (
                                                        <p className="text-label text-primary">{errors.members[index].user.company}</p>
                                                    )}
                                                </div>
                                                <div className={`
                                                    flex-grow gap-2 rounded-[8px] border 
                                                    ${(!!vatErrors[values.members[index].user.afm])? "border-primary border-2" : "border-[#A5A5A5]"} 
                                                    p-2 bg-white text-[#A5A5A5]'`}>
                                                    <input
                                                        disabled={!!shadow.id || (isAddingLoading || isUpdatingLoading)}
                                                        className="outline-0 w-full  text-black placeholder:text-label"
                                                        name={`afm-${index}`}
                                                        value={values.members[index].user.afm}
                                                        onChange={(e) =>
                                                            setFieldValue(
                                                                `members[${index}].user.afm`,
                                                                e.target.value
                                                            )
                                                        }
                                                        placeholder={`π.χ. 123456789`}
                                                    />
                                                    {!!vatErrors[values.members[index].user.afm] && (
                                                        <p className="text-label text-primary">{vatErrors[values.members[index].user.afm]}</p>
                                                    )}
                                                </div>
                                                <Dialog
                                                    open={unassignOpenModal}
                                                    className={clsx('lg:max-h-[80vh] dialog-wrapper')}
                                                    >
                                                    <DialogTrigger
                                                        onClick={(value) => {
                                                            setShadowToUnassign(shadow);
                                                            if(!shadow.id) {
                                                                arrayHelpers.remove(index);
                                                                // setMembers(values.members.filter(member => member !== shadow));
                                                            } else {
                                                                setUnassignOpenModal(shadow.id);
                                                            }
                                                        }}
                                                        className={''}
                                                        disabled={false}
                                                    >
                                                        <TrashIcon width={22} height={25}/>
                                                    </DialogTrigger>
                                                    <DialogContent blurred={true} className={clsx('', 'max-lg:h-[100vh]')}>
                                                        <ConfirmationDialog
                                                            message={`Είστε σίγουροι ότι θέλετε να διαγράψετε το Α.Φ.Μ. ${shadowToUnassign?.user?.afm};`}
                                                            title="Διαγραφή Α.Φ.Μ."
                                                            handleOkClick= {()=> {
                                                                handleUnassignGroupMember(shadowToUnassign);
                                                            }}
                                                            isLoading={isUnassignMemberLoading}
                                                            handleCancelClick={() => setUnassignOpenModal(false)}
                                                        />
                                                    </DialogContent>
                                                </Dialog>
                                            </div>
                                            ))}
                                            <div className='flex flex-col gap-y-2 pt-4'>
                                                <button
                                                    disabled={(isAddingLoading || isUpdatingLoading)}
                                                    type="button"
                                                    onClick={() => arrayHelpers.push({user:{afm:"",company:""}})}
                                                    className="border-2 rounded-lg border-dashed border-fadedgrey text-button-primary w-full max-lg:w-full"
                                                >
                                                    <div className="flex items-center justify-center gap-2">
                                                        <p className='py-2'>Προσθήκη ΑΦΜ</p>
                                                        <PlusIcon fill="#A5A5A5"/>
                                                    </div>
                                                </button>
                                            </div>
                                        </div>
                                        )}
                                    />
                                    <div className="col-span-2 flex justify-center gap-x-3.5 py-6">
                                        <ModalFooter
                                            disabled={(isAddingLoading || isUpdatingLoading)}
                                            handleCancelClick={handleClose}
                                            type="submit"
                                            className="max-lg:flex-col max-lg:col-reverse max-lg:w-full"
                                            isLoading={(isAddingLoading || isUpdatingLoading)}
                                            saveBtnText= {(modalMode==="edit")? "Ενημέρωση" : "Δημιουργία"}
                                            />
                                    </div>
                                </>
                            </form>
                        )}
                    </Formik>
                </div>
                
            </div>
        </>
    );
}